// @contexts/FeedbackClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from "react";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { FeedbackClient } from "@services/clients/FeedbackClient";
import { fromInteropFeedbackClient } from "@services/clients/FeedbackClient";
import { createDefaultFeedbackClient } from "@src/purescript/interop";
import { StubFeedbackClient } from "@services/stubs/StubFeedbackClient";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import { useJwtController } from "@contexts/Jwt.context";
import { requireContext } from "@contexts/requireContext";

const FeedbackClientContext = createContext<OptionTs<FeedbackClient>>(Option.none());

export const FeedbackClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { markUnauthorized } = useJwtController();
  const feedbackClient = useMemo<FeedbackClient>(() => {
    if (enabledFeatures[FeaturesEnum.StubClients]) {
      return new StubFeedbackClient();
    }
    return fromInteropFeedbackClient(createDefaultFeedbackClient(markUnauthorized));
  }, [markUnauthorized]);

  const contextValue = useMemo(() => Option.some(feedbackClient), [feedbackClient]);

  return (
    <FeedbackClientContext.Provider value={contextValue}>
      {children}
    </FeedbackClientContext.Provider>
  );
};

export const useFeedbackClient = (): FeedbackClient => {
  return requireContext(
    useContext(FeedbackClientContext),
    "useFeedbackClient must be used within a FeedbackClientProvider",
  );
};
